import { apiDelete, apiGet, apiPost, apiPut } from "./client";

export interface ShopifyConnection {
  id: string;
  shopDomain: string;
  shopName?: string | null;
  status: "active" | "reauthorization_required" | "error" | "disconnected";
  customerId?: string | null;
  companyId?: string | null;
  pickupLocationId?: string | null;
  defaultProjectNumber?: string | null;
  defaultDriverId?: string | null;
  defaultProductId?: string | null;
  productSyncEnabled: boolean;
  importEnabled: boolean;
  importMode: "local_delivery" | "all_shippable";
  webhookStatus?: "active" | "error" | "disabled" | null;
  lastWebhookAt?: string | null;
  lastSuccessfulSyncAt?: string | null;
  lastErrorCode?: string | null;
}

export const linkShopifyInstallation = async (
  token: string,
  workspaceId: string,
  state: string,
  pickupLocationId: string,
): Promise<void> => {
  await apiPost(
    token,
    "/api/shopify/installations/link",
    { state, workspaceId, pickupLocationId },
    workspaceId,
  );
};

export interface ShopifyConnectionConfiguration {
  customerId?: string | null;
  companyId?: string | null;
  pickupLocationId: string;
  defaultProjectNumber?: string | null;
  defaultDriverId?: string | null;
  defaultProductId?: string | null;
  productSyncEnabled: boolean;
  importEnabled: boolean;
  importMode: "local_delivery" | "all_shippable";
}

export interface ShopifyProductMappingItem {
  variant: {
    productId: string;
    variantId: string;
    productTitle: string;
    variantTitle?: string | null;
    sku?: string | null;
    weightKg?: number | null;
  };
  status: "mapped" | "suggested" | "unmapped" | "ignored";
  logibudProductId?: string | null;
  suggestedLogibudProductId?: string | null;
  warningCode?: string | null;
}

export interface ShopifyProductMappingPreview {
  items: ShopifyProductMappingItem[];
  mappedCount: number;
  suggestedCount: number;
  unmappedCount: number;
  ignoredCount: number;
}

export interface ShopifyImportEvent {
  id: string;
  status:
    | "received"
    | "processing"
    | "succeeded"
    | "retry_pending"
    | "failed"
    | "dismissed";
  attemptCount: number;
  receivedAt?: string | null;
  processedAt?: string | null;
  nextAttemptAt?: string | null;
  lastErrorCode?: string | null;
  retryable: boolean;
}

export interface ShopifyImportHealth {
  connectionId: string;
  status: "healthy" | "pending" | "attention" | "idle" | "paused";
  recentEventCount: number;
  succeededCount: number;
  pendingCount: number;
  failedCount: number;
  events: ShopifyImportEvent[];
}

export const startShopifyOAuth = async (
  token: string,
  workspaceId: string,
  shopDomain: string,
): Promise<{ authorizationUrl: string; expiresIn: number }> => {
  const response = await apiPost(
    token,
    "/api/shopify/oauth/start",
    { shopDomain },
    workspaceId,
  );
  return response.data;
};

export const listShopifyConnections = async (
  token: string,
  workspaceId: string,
): Promise<ShopifyConnection[]> => {
  const response = await apiGet(token, "/api/shopify/connections", workspaceId);
  return response?.data ?? [];
};

export const updateShopifyConnection = async (
  token: string,
  workspaceId: string,
  connectionId: string,
  configuration: ShopifyConnectionConfiguration,
): Promise<ShopifyConnection> => {
  const response = await apiPut(
    token,
    `/api/shopify/connections/${connectionId}`,
    configuration,
    workspaceId,
  );
  return response.data;
};

export const disconnectShopifyConnection = async (
  token: string,
  workspaceId: string,
  connectionId: string,
): Promise<ShopifyConnection> => {
  const response = await apiDelete(
    token,
    `/api/shopify/connections/${connectionId}`,
    workspaceId,
  );
  return response.data;
};

export const previewShopifyProducts = async (
  token: string,
  workspaceId: string,
  connectionId: string,
): Promise<ShopifyProductMappingPreview> => {
  const response = await apiGet(
    token,
    `/api/shopify/connections/${connectionId}/products/preview`,
    workspaceId,
  );
  return response.data;
};

export const saveShopifyProductMappings = async (
  token: string,
  workspaceId: string,
  connectionId: string,
  mappings: Array<{
    shopifyVariantId: string;
    logibudProductId?: string | null;
    ignored: boolean;
  }>,
): Promise<ShopifyProductMappingPreview> => {
  const response = await apiPut(
    token,
    `/api/shopify/connections/${connectionId}/products`,
    { mappings },
    workspaceId,
  );
  return response.data;
};

export const getShopifyImportHealth = async (
  token: string,
  workspaceId: string,
  connectionId: string,
): Promise<ShopifyImportHealth> => {
  const response = await apiGet(
    token,
    `/api/shopify/connections/${connectionId}/imports/health`,
    workspaceId,
  );
  return response.data;
};

export const retryShopifyImport = async (
  token: string,
  workspaceId: string,
  connectionId: string,
  eventId: string,
): Promise<ShopifyImportEvent> => {
  const response = await apiPost(
    token,
    `/api/shopify/connections/${connectionId}/imports/${eventId}/retry`,
    {},
    workspaceId,
  );
  return response.data;
};

export const dismissShopifyImport = async (
  token: string,
  workspaceId: string,
  connectionId: string,
  eventId: string,
): Promise<ShopifyImportEvent> => {
  const response = await apiPost(
    token,
    `/api/shopify/connections/${connectionId}/imports/${eventId}/dismiss`,
    {},
    workspaceId,
  );
  return response.data;
};

export const restoreShopifyImport = async (
  token: string,
  workspaceId: string,
  connectionId: string,
  eventId: string,
): Promise<ShopifyImportEvent> => {
  const response = await apiPost(
    token,
    `/api/shopify/connections/${connectionId}/imports/${eventId}/restore`,
    {},
    workspaceId,
  );
  return response.data;
};
